import { Controller } from "@hotwired/stimulus";
import {
  hideRailsuiModal,
  showRailsuiModal,
} from "../../../../utils/railsui_modal";
import { remainingFromTimestamps } from "../../../../utils/remaining_from_timestamps";

const FROZEN_TIMER_KEY = "assessmentOfflineFrozenTimer";

const MODAL_IDS = {
  pauseRationale: "calibrate-pause-rationale-modal",
  warning: "calibrate-warning-modal",
  expiration: "calibrate-expiration-modal",
  pauseExpiration: "calibrate-pause-expiration-modal",
  resumeAssessment: "calibrate-resume-assessment-modal",
};

const PAUSE_REQUEST_FAILED =
  "Unable to pause your assessment. Check your connection and try again.";
const RESUME_REQUEST_FAILED =
  "Unable to resume your assessment. Check your connection and try again.";
const AUTOSUBMIT_REQUEST_FAILED =
  "Unable to submit your assessment. Close to try again.";

function formatHmsDisplay(remaining) {
  const hours = remaining.hours.toString();
  const minutes = remaining.minutes.toString().padStart(2, "0");
  const seconds = remaining.seconds.toString().padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

export default class extends Controller {
  static targets = [
    "display",
    "pauseTime",
    "examTimer",
    "pauseTimer",
    "pauseButton",
    "timedout",
    "pauseUrl",
    "resumeUrl",
    "autosubmitUrl",
    "redirectUrl",
    "errorMessage",
  ];

  static values = {
    rosteringId: Number,
    startingSeconds: Number,
    warningSeconds: Number,
    pauseSeconds: Number,
    pauseRemainingSeconds: Number,
  };

  initialize() {
    if (!this.hasDisplayTarget && !this.hasPauseTimeTarget) return;

    this.examSeconds = this.startingSecondsValue;
    this.warningSeconds = this.warningSecondsValue;
    this.serverPauseRemaining = this.pauseRemainingSecondsValue;
    this.autosubmitCompleted = false;
    this.autosubmitInFlight = null;

    const frozenSeconds = this.consumeFrozenRemainingSeconds();
    if (frozenSeconds != null) {
      this.examSeconds = frozenSeconds;
      const originalStarting = this.startingSecondsValue;
      const originalWarning = this.warningSecondsValue;
      if (Number.isFinite(originalStarting) && Number.isFinite(originalWarning)) {
        this.warningSeconds = Math.max(
          0,
          frozenSeconds - (originalStarting - originalWarning),
        );
      }
    }

    this.startExamCountdown();
    this.startWarningCountdown();
    if (this.hasPauseTimerTarget) {
      this.pauseTimerTarget.hidden = true;
    }

    if (this.pauseRemainingSecondsValue > 0) {
      this.pausesExam(this.pauseRemainingSecondsValue);
      this.openModal(MODAL_IDS.resumeAssessment, { disableEscape: true });
    }
  }

  connect() {
    this.offlineQueued = () => this.stopsExamOffline();
    window.addEventListener("assessment-offline:queued", this.offlineQueued);
  }

  disconnect() {
    if (this.examInterval) clearInterval(this.examInterval);
    if (this.warningInterval) clearInterval(this.warningInterval);
    if (this.pauseCountdownHandle) clearInterval(this.pauseCountdownHandle);
    if (this.boundResumeExamClock) {
      window.removeEventListener("online", this.boundResumeExamClock);
      this.boundResumeExamClock = null;
    }
    if (this.offlineQueued) {
      window.removeEventListener("assessment-offline:queued", this.offlineQueued);
    }
  }

  showPauseRationaleModal(event) {
    event?.preventDefault();
    this.openModal(MODAL_IDS.pauseRationale);
    const reason = this.pauseReasonField();
    reason?.focus();
  }

  cancelPauseRationale(event) {
    event?.preventDefault();
    this.closeModal(MODAL_IDS.pauseRationale);
  }

  async submitPauseRationale(event) {
    event?.preventDefault();
    const reason = this.pauseReasonField();
    if (!reason) return;

    const pauseReason = reason.value.trim();
    const paused = await this.pause(pauseReason);
    if (!paused) return;

    this.closeModal(MODAL_IDS.pauseRationale);
    this.openModal(MODAL_IDS.resumeAssessment, { disableEscape: true });
    this.hidePauseButton();
  }

  async resumeFromPause(event) {
    event?.preventDefault();
    this.suppressPauseModalDismissResume = true;
    this.closeModal(MODAL_IDS.resumeAssessment);
    await this.pauseModalClosed();
    this.suppressPauseModalDismissResume = false;
  }

  pauseModalDismissed(_event) {
    if (this.suppressPauseModalDismissResume) return;
    if (!this.pauseCountdownHandle) return;

    void this.pauseModalClosed();
  }

  closeWarning(event) {
    event?.preventDefault();
    this.closeModal(MODAL_IDS.warning);
  }

  async closeExpiration(event) {
    event?.preventDefault();
    await this.finishAutosubmitAndRedirect();
  }

  async closePauseExpiration(event) {
    event?.preventDefault();
    this.closeModal(MODAL_IDS.pauseExpiration);
    await this.finishAutosubmitAndRedirect();
  }

  consumeFrozenRemainingSeconds() {
    try {
      const raw = localStorage.getItem(FROZEN_TIMER_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw);
      const rosteringId = this.rosteringIdValue;
      if (!rosteringId || Number(data.rosteringId) !== rosteringId) return null;

      const remainingSeconds = Number(data.remainingSeconds);
      if (!Number.isFinite(remainingSeconds) || remainingSeconds < 0) return null;

      const savedAt = Number(data.savedAt) || Date.now();
      const elapsed = navigator.onLine ? Math.max(0, (Date.now() - savedAt) / 1000) : 0;
      const adjusted = Math.max(0, Math.floor(remainingSeconds - elapsed));
      const serverSeconds = this.startingSecondsValue;
      if (Number.isFinite(serverSeconds) && adjusted - serverSeconds <= 2) {
        localStorage.removeItem(FROZEN_TIMER_KEY);
        return null;
      }

      localStorage.setItem(
        FROZEN_TIMER_KEY,
        JSON.stringify({ ...data, remainingSeconds: adjusted, savedAt: Date.now() }),
      );
      return adjusted;
    } catch (_error) {
      return null;
    }
  }

  snapshotExamClock({ updateDisplay = true } = {}) {
    const remainingMs = this.examEndTime
      ? Math.max(0, this.examEndTime - Date.now())
      : Math.max(0, (this.examSeconds || 0) * 1000);
    const remainingSeconds = Math.ceil(remainingMs / 1000);

    this.examSeconds = remainingSeconds;
    this.examEndTime = Date.now() + remainingSeconds * 1000;

    const originalStarting = this.startingSecondsValue;
    const originalWarning = this.warningSecondsValue;
    if (Number.isFinite(originalStarting) && Number.isFinite(originalWarning)) {
      this.warningSeconds = Math.max(
        0,
        remainingSeconds - (originalStarting - originalWarning),
      );
    }

    if (updateDisplay && this.hasDisplayTarget) {
      this.updateDisplay(remainingFromTimestamps(Date.now(), this.examEndTime));
    }

    return remainingSeconds;
  }

  freezeExamClock() {
    const remainingSeconds = this.snapshotExamClock();

    if (this.examInterval) clearInterval(this.examInterval);
    if (this.warningInterval) clearInterval(this.warningInterval);
    this.examInterval = null;
    this.warningInterval = null;

    this.examClockFrozen = true;
    this.ensureOnlineResumeListener();
    return remainingSeconds;
  }

  ensureOnlineResumeListener() {
    if (this.boundResumeExamClock) return;
    this.boundResumeExamClock = () => this.resumeExamClockIfFrozen();
    window.addEventListener("online", this.boundResumeExamClock);
  }

  resumeExamClockIfFrozen() {
    if (!this.examClockFrozen) return;
    if (!navigator.onLine) return;

    this.examClockFrozen = false;
    this.startExamCountdown();
    this.startWarningCountdown();
  }

  persistTrackedRemaining(remainingSeconds) {
    try {
      const raw = localStorage.getItem(FROZEN_TIMER_KEY);
      if (!raw) return;

      const data = JSON.parse(raw);
      const rosteringId = this.rosteringIdValue;
      if (!rosteringId || Number(data.rosteringId) !== rosteringId) return;

      localStorage.setItem(
        FROZEN_TIMER_KEY,
        JSON.stringify({
          ...data,
          remainingSeconds: Math.max(0, Math.floor(remainingSeconds)),
          savedAt: Date.now(),
          frozen: false,
        }),
      );
    } catch (_error) {
    }
  }

  startExamCountdown() {
    if (this.examInterval) clearInterval(this.examInterval);

    this.examEndTime = Date.now() + this.examSeconds * 1000;

    const tickExam = () => {
      if (!this.hasDisplayTarget) {
        clearInterval(this.examInterval);
        return;
      }

      const now = Date.now();
      const timeLeft = this.examEndTime - now;

      if (timeLeft <= 0) {
        clearInterval(this.examInterval);
        this.persistTrackedRemaining(0);
        this.onExpired();
        return;
      }
      this.persistTrackedRemaining(timeLeft / 1000);
      const remaining = remainingFromTimestamps(now, now + timeLeft);
      this.updateDisplay(remaining);
    };

    tickExam();
    this.examInterval = setInterval(tickExam, 1000);
  }

  startWarningCountdown() {
    if (this.warningInterval) clearInterval(this.warningInterval);

    this.warningEndTime = Date.now() + this.warningSeconds * 1000;
    this.warningInterval = setInterval(() => {
      const timeLeft = this.warningEndTime - Date.now();

      if (timeLeft <= 0) {
        this.displayWarning();
        this.displayWarningModal(timeLeft);
        clearInterval(this.warningInterval);
      }
    }, 1000);
  }

  updateDisplay(remaining) {
    if (!this.hasDisplayTarget) return;

    this.displayTarget.textContent = formatHmsDisplay(remaining);
  }

  pauseReasonField() {
    return document.getElementById("calibrate-pause-reason");
  }

  async pause(pauseReason) {
    const pauseUrl = this.pauseUrlTarget?.value;
    if (!pauseUrl) return false;

    try {
      const response = await fetch(pauseUrl, {
        method: "PATCH",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ rostering: { pause_reason: pauseReason } }),
      });
      if (!response.ok) {
        this.showTimerError(PAUSE_REQUEST_FAILED);
        return false;
      }

      this.hideTimerError();
      this.pausesExam(this.pauseSecondsValue);
      return true;
    } catch (_error) {
      this.showTimerError(PAUSE_REQUEST_FAILED);
      return false;
    }
  }

  stopsExamOffline() {
    clearInterval(this.examInterval);
    clearInterval(this.warningInterval);
    this.hidePauseButton();
  }

  pausesExam(pauseSeconds) {
    this.snapshotExamClock({ updateDisplay: false });

    if (this.pauseCountdownHandle) clearInterval(this.pauseCountdownHandle);

    this.pauseEndTime = Date.now() + pauseSeconds * 1000;
    this.pauseCountdownHandle = setInterval(() => {
      const remaining = remainingFromTimestamps(Date.now(), this.pauseEndTime);
      if (remaining.value <= 0) {
        this.submitExam();
        clearInterval(this.pauseCountdownHandle);
        this.pauseCountdownHandle = null;
      } else {
        this.displayPauseTimer(remaining);
      }
    }, 1000);

    clearInterval(this.examInterval);
    clearInterval(this.warningInterval);
    this.examInterval = null;
    this.warningInterval = null;

    if (this.hasPauseTimerTarget) this.pauseTimerTarget.hidden = false;
    if (this.hasExamTimerTarget) this.examTimerTarget.hidden = true;
    this.hidePauseButton();
  }

  async pauseModalClosed() {
    await this.resume();
  }

  async resume() {
    const resumeUrl = this.resumeUrlTarget?.value;
    if (!resumeUrl) return;

    try {
      const response = await fetch(resumeUrl, {
        method: "PATCH",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ rostering: {} }),
      });
      if (!response.ok) {
        this.showTimerError(RESUME_REQUEST_FAILED);
        return;
      }

      this.hideTimerError();
      clearInterval(this.pauseCountdownHandle);
      this.pauseCountdownHandle = null;

      this.startExamCountdown();
      this.startWarningCountdown();

      if (this.hasPauseTimerTarget) this.pauseTimerTarget.hidden = true;
      if (this.hasExamTimerTarget) this.examTimerTarget.hidden = false;
    } catch (_error) {
      this.showTimerError(RESUME_REQUEST_FAILED);
    }
  }

  submitExam() {
    if (this.hasTimedoutTarget) {
      this.timedoutTarget.value = "timedout";
    }
    this.suppressPauseModalDismissResume = true;
    this.closeModal(MODAL_IDS.resumeAssessment);
    this.suppressPauseModalDismissResume = false;
    this.openModal(MODAL_IDS.pauseExpiration, { disableEscape: true });
    void this.requestAutosubmit();
  }

  displayPauseTimer(remaining) {
    if (!this.hasPauseTimeTarget) return;

    this.pauseTimeTarget.textContent = formatHmsDisplay(remaining);
  }

  onExpired() {
    clearInterval(this.examInterval);
    this.openModal(MODAL_IDS.expiration, { disableEscape: true });
    if (this.hasTimedoutTarget) {
      this.timedoutTarget.value = "timedout";
    }
    void this.requestAutosubmit();
  }

  displayWarning() {
    this.displayTarget?.classList.add("text-(--ds-text-error)");
  }

  displayWarningModal(timeLeft) {
    if (timeLeft > -2000) {
      this.openModal(MODAL_IDS.warning);
    }
  }

  async finishAutosubmitAndRedirect() {
    if (!this.autosubmitCompleted) {
      await this.requestAutosubmit();
    }
    if (!this.autosubmitCompleted) return;

    const redirectUrl = this.redirectUrlTarget?.value;
    if (redirectUrl) {
      window.location = redirectUrl;
    }
  }

  requestAutosubmit() {
    if (this.autosubmitCompleted) return Promise.resolve(true);
    if (this.autosubmitInFlight) return this.autosubmitInFlight;

    const autoSubmitUrl = this.autosubmitUrlTarget?.value;
    if (!autoSubmitUrl) return Promise.resolve(false);

    this.autosubmitInFlight = (async () => {
      try {
        const response = await fetch(autoSubmitUrl, {
          method: "PATCH",
          headers: this.jsonHeaders(),
        });
        if (!response.ok) {
          this.showTimerError(AUTOSUBMIT_REQUEST_FAILED);
          return false;
        }

        this.autosubmitCompleted = true;
        this.hideTimerError();
        return true;
      } catch (_error) {
        this.showTimerError(AUTOSUBMIT_REQUEST_FAILED);
        return false;
      } finally {
        this.autosubmitInFlight = null;
      }
    })();

    return this.autosubmitInFlight;
  }

  showTimerError(message) {
    if (!this.hasErrorMessageTarget) return;

    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.hidden = false;
  }

  hideTimerError() {
    if (!this.hasErrorMessageTarget) return;

    this.errorMessageTarget.textContent = "";
    this.errorMessageTarget.hidden = true;
  }

  hidePauseButton() {
    if (!this.hasPauseButtonTarget) return;
    this.pauseButtonTarget.hidden = true;
  }

  openModal(modalId, { disableEscape = false } = {}) {
    showRailsuiModal(document.getElementById(modalId), { disableEscape });
  }

  closeModal(modalId) {
    hideRailsuiModal(document.getElementById(modalId));
  }

  jsonHeaders() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": token,
    };
  }
}
